import * as THREE from 'three';

/**
 * Geometry repair utilities that fix degenerate triangles by merging vertices
 * instead of removing triangles (which creates holes)
 */

const VERTEX_MERGE_THRESHOLD = 1e-4; // Merge vertices closer than this distance
const MIN_EDGE_LENGTH = 1e-4; // Collapse edges shorter than this

/**
 * Merge vertices that are extremely close together
 * This fixes degenerate triangles without creating holes
 * @param {THREE.BufferGeometry} geometry - The geometry to repair
 * @param {number} threshold - Distance threshold for merging
 * @param {Uint8Array} meshBoundaryVertices - Optional: boundary vertices to preserve
 */
export function mergeCloseVertices(geometry, threshold = VERTEX_MERGE_THRESHOLD, meshBoundaryVertices = null) {
  const positions = geometry.attributes.position;
  const indices = geometry.index ? geometry.index.array : null;

  if (!indices) {
    console.warn('[geometryRepair] Geometry must be indexed for vertex merging');
    return geometry;
  }

  const vertexCount = positions.count;
  const posArray = positions.array;

  // Build spatial hash for fast proximity queries
  const cellSize = threshold * 2;
  const spatialHash = new Map();

  function hashVertex(x, y, z) {
    const cx = Math.floor(x / cellSize);
    const cy = Math.floor(y / cellSize);
    const cz = Math.floor(z / cellSize);
    return `${cx},${cy},${cz}`;
  }

  // Map old vertex index to new merged vertex index
  const vertexMapping = new Array(vertexCount);
  for (let i = 0; i < vertexCount; i++) {
    vertexMapping[i] = i; // Initially, each vertex maps to itself
  }

  let mergeCount = 0;

  // Process each vertex
  for (let i = 0; i < vertexCount; i++) {
    // Skip mesh boundary vertices - they must not be merged
    if (meshBoundaryVertices && meshBoundaryVertices[i]) {
      const x = posArray[i * 3];
      const y = posArray[i * 3 + 1];
      const z = posArray[i * 3 + 2];
      const hash = hashVertex(x, y, z);

      // Add to hash but don't merge
      if (!spatialHash.has(hash)) {
        spatialHash.set(hash, [i]);
      } else {
        spatialHash.get(hash).push(i);
      }
      continue;
    }

    const x = posArray[i * 3];
    const y = posArray[i * 3 + 1];
    const z = posArray[i * 3 + 2];

    const hash = hashVertex(x, y, z);

    // Check if there's already a vertex in this cell
    if (spatialHash.has(hash)) {
      const candidates = spatialHash.get(hash);

      // Find the closest existing vertex within threshold
      // Skip boundary vertices as merge targets
      let closestIdx = -1;
      let closestDist = threshold;

      for (const candidateIdx of candidates) {
        // Don't merge into boundary vertices
        if (meshBoundaryVertices && meshBoundaryVertices[candidateIdx]) {
          continue;
        }

        const cx = posArray[candidateIdx * 3];
        const cy = posArray[candidateIdx * 3 + 1];
        const cz = posArray[candidateIdx * 3 + 2];

        const dx = x - cx;
        const dy = y - cy;
        const dz = z - cz;
        const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

        if (dist < closestDist) {
          closestDist = dist;
          closestIdx = candidateIdx;
        }
      }

      if (closestIdx !== -1) {
        // Merge this vertex with the closest one
        vertexMapping[i] = closestIdx;
        mergeCount++;
      } else {
        // No close vertex found, add this one to the hash
        candidates.push(i);
      }
    } else {
      // First vertex in this cell
      spatialHash.set(hash, [i]);
    }
  }

  if (mergeCount === 0) {
    console.log('[geometryRepair] No vertices needed merging');
    return geometry;
  }

  console.log(`[geometryRepair] Merged ${mergeCount} vertices (${vertexCount} -> ${vertexCount - mergeCount})`);

  // Apply vertex mapping to indices
  const newIndices = [];
  let removedTriangles = 0;

  for (let i = 0; i < indices.length; i += 3) {
    const i0 = vertexMapping[indices[i]];
    const i1 = vertexMapping[indices[i + 1]];
    const i2 = vertexMapping[indices[i + 2]];

    // Skip degenerate triangles (where all vertices are the same after merging)
    if (i0 !== i1 && i1 !== i2 && i2 !== i0) {
      newIndices.push(i0, i1, i2);
    } else {
      removedTriangles++;
    }
  }

  console.log(`[geometryRepair] Removed ${removedTriangles} degenerate triangles after vertex merge`);

  // Update geometry indices
  geometry.setIndex(newIndices);

  // Note: We keep all original vertices in the position buffer
  // Three.js will only render vertices referenced by indices
  // This is more efficient than rebuilding the entire vertex buffer

  return geometry;
}

/**
 * Collapse short edges to fix thin triangles
 * Merges endpoint vertices of edges shorter than threshold
 * @param {THREE.BufferGeometry} geometry - The geometry to repair
 * @param {number} threshold - Length threshold for collapsing
 * @param {Uint8Array} meshBoundaryVertices - Optional: boundary vertices to preserve
 */
export function collapseShortEdges(geometry, threshold = MIN_EDGE_LENGTH, meshBoundaryVertices = null) {
  const positions = geometry.attributes.position;
  const indices = geometry.index ? geometry.index.array : null;

  if (!indices) {
    console.warn('[geometryRepair] Geometry must be indexed for edge collapse');
    return geometry;
  }

  const posArray = positions.array;

  // Build edge map
  const edgeMap = new Map();

  for (let i = 0; i < indices.length; i += 3) {
    const i0 = indices[i];
    const i1 = indices[i + 1];
    const i2 = indices[i + 2];

    const edges = [
      [i0, i1],
      [i1, i2],
      [i2, i0]
    ];

    edges.forEach(([a, b]) => {
      const key = `${Math.min(a, b)}-${Math.max(a, b)}`;
      if (!edgeMap.has(key)) {
        edgeMap.set(key, [a, b]);
      }
    });
  }

  // Find short edges (excluding boundary edges)
  const shortEdges = [];

  for (const [key, [a, b]] of edgeMap) {
    // Skip if either endpoint is a boundary vertex
    if (meshBoundaryVertices && (meshBoundaryVertices[a] || meshBoundaryVertices[b])) {
      continue;
    }

    const ax = posArray[a * 3];
    const ay = posArray[a * 3 + 1];
    const az = posArray[a * 3 + 2];
    const bx = posArray[b * 3];
    const by = posArray[b * 3 + 1];
    const bz = posArray[b * 3 + 2];

    const dx = bx - ax;
    const dy = by - ay;
    const dz = bz - az;
    const length = Math.sqrt(dx * dx + dy * dy + dz * dz);

    if (length < threshold) {
      shortEdges.push([a, b, length]);
    }
  }

  if (shortEdges.length === 0) {
    console.log('[geometryRepair] No short edges found');
    return geometry;
  }

  console.log(`[geometryRepair] Found ${shortEdges.length} short edges to collapse`);

  // Sort by length (collapse shortest first)
  shortEdges.sort((a, b) => a[2] - b[2]);

  // Create vertex mapping
  const vertexMapping = new Array(positions.count);
  for (let i = 0; i < positions.count; i++) {
    vertexMapping[i] = i;
  }

  // Collapse edges by merging vertices
  for (const [a, b] of shortEdges) {
    // Merge b into a (keep a, redirect b)
    const finalA = vertexMapping[a];
    const finalB = vertexMapping[b];

    if (finalA !== finalB) {
      // Redirect all references to finalB to point to finalA
      for (let i = 0; i < vertexMapping.length; i++) {
        if (vertexMapping[i] === finalB) {
          vertexMapping[i] = finalA;
        }
      }
    }
  }

  // Apply mapping and remove degenerate triangles
  const newIndices = [];
  let removedTriangles = 0;

  for (let i = 0; i < indices.length; i += 3) {
    const i0 = vertexMapping[indices[i]];
    const i1 = vertexMapping[indices[i + 1]];
    const i2 = vertexMapping[indices[i + 2]];

    if (i0 !== i1 && i1 !== i2 && i2 !== i0) {
      newIndices.push(i0, i1, i2);
    } else {
      removedTriangles++;
    }
  }

  console.log(`[geometryRepair] Collapsed ${shortEdges.length} edges, removed ${removedTriangles} degenerate triangles`);

  geometry.setIndex(newIndices);
  return geometry;
}

/**
 * Smooth vertex positions in areas with high geometric distortion
 * Uses Laplacian smoothing with constraints
 * @param {THREE.BufferGeometry} geometry - The geometry to repair
 * @param {number} iterations - Number of smoothing iterations
 * @param {number} lambda - Smoothing strength (0-1)
 * @param {Uint8Array} meshBoundaryVertices - Optional: boundary vertices to preserve
 */
export function smoothDistortedAreas(geometry, iterations = 3, lambda = 0.5, meshBoundaryVertices = null) {
  const positions = geometry.attributes.position;
  const indices = geometry.index ? geometry.index.array : null;

  if (!indices) {
    console.warn('[geometryRepair] Geometry must be indexed for smoothing');
    return geometry;
  }

  const vertexCount = positions.count;
  const posArray = positions.array;

  // Build adjacency
  const adjacency = new Map();
  for (let i = 0; i < vertexCount; i++) {
    adjacency.set(i, new Set());
  }

  for (let i = 0; i < indices.length; i += 3) {
    const i0 = indices[i];
    const i1 = indices[i + 1];
    const i2 = indices[i + 2];

    adjacency.get(i0).add(i1);
    adjacency.get(i0).add(i2);
    adjacency.get(i1).add(i0);
    adjacency.get(i1).add(i2);
    adjacency.get(i2).add(i0);
    adjacency.get(i2).add(i1);
  }

  // Identify distorted vertices (those with very short edges)
  // Exclude boundary vertices
  const distortedVertices = new Set();
  const DISTORTION_THRESHOLD = 1e-3;

  for (let i = 0; i < vertexCount; i++) {
    // Skip boundary vertices - they must not be smoothed
    if (meshBoundaryVertices && meshBoundaryVertices[i]) {
      continue;
    }

    const neighbors = adjacency.get(i);
    const x = posArray[i * 3];
    const y = posArray[i * 3 + 1];
    const z = posArray[i * 3 + 2];

    for (const neighborIdx of neighbors) {
      const nx = posArray[neighborIdx * 3];
      const ny = posArray[neighborIdx * 3 + 1];
      const nz = posArray[neighborIdx * 3 + 2];

      const dx = nx - x;
      const dy = ny - y;
      const dz = nz - z;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

      if (dist < DISTORTION_THRESHOLD) {
        distortedVertices.add(i);
        // Don't add neighbor if it's a boundary vertex
        if (!meshBoundaryVertices || !meshBoundaryVertices[neighborIdx]) {
          distortedVertices.add(neighborIdx);
        }
      }
    }
  }

  if (distortedVertices.size === 0) {
    console.log('[geometryRepair] No distorted areas found');
    return geometry;
  }

  console.log(`[geometryRepair] Smoothing ${distortedVertices.size} distorted vertices`);

  // Apply Laplacian smoothing to distorted areas
  const newPositions = new Float32Array(posArray);

  for (let iter = 0; iter < iterations; iter++) {
    const tempPositions = new Float32Array(newPositions);

    for (const vertexIdx of distortedVertices) {
      const neighbors = adjacency.get(vertexIdx);
      if (neighbors.size === 0) continue;

      let avgX = 0, avgY = 0, avgZ = 0;

      for (const neighborIdx of neighbors) {
        avgX += newPositions[neighborIdx * 3];
        avgY += newPositions[neighborIdx * 3 + 1];
        avgZ += newPositions[neighborIdx * 3 + 2];
      }

      avgX /= neighbors.size;
      avgY /= neighbors.size;
      avgZ /= neighbors.size;

      // Blend between original and averaged
      const i3 = vertexIdx * 3;
      tempPositions[i3] = newPositions[i3] * (1 - lambda) + avgX * lambda;
      tempPositions[i3 + 1] = newPositions[i3 + 1] * (1 - lambda) + avgY * lambda;
      tempPositions[i3 + 2] = newPositions[i3 + 2] * (1 - lambda) + avgZ * lambda;
    }

    newPositions.set(tempPositions);
  }

  // Update geometry
  positions.array.set(newPositions);
  positions.needsUpdate = true;

  return geometry;
}

/**
 * Main repair function - attempts multiple strategies to fix degenerate geometry
 * without creating holes
 * @param {THREE.BufferGeometry} geometry - The geometry to repair
 * @param {Object} options - Repair options
 * @param {boolean} options.mergeVertices - Merge close vertices
 * @param {boolean} options.collapseEdges - Collapse short edges
 * @param {boolean} options.smoothDistorted - Smooth distorted areas
 * @param {boolean} options.verbose - Log repair operations
 * @param {Uint8Array} options.meshBoundaryVertices - Boundary vertices to preserve
 */
export function repairGeometry(geometry, options = {}) {
  const {
    mergeVertices = true,
    collapseEdges = true,
    smoothDistorted = true,
    verbose = true,
    meshBoundaryVertices = null
  } = options;

  if (!geometry || !geometry.attributes.position) {
    console.warn('[geometryRepair] Invalid geometry provided');
    return geometry;
  }

  const startTriangleCount = geometry.index ? geometry.index.count / 3 : 0;

  if (verbose && meshBoundaryVertices) {
    const boundaryCount = meshBoundaryVertices.reduce((sum, val) => sum + val, 0);
    console.log(`[geometryRepair] Preserving ${boundaryCount} mesh boundary vertices`);
  }

  // Strategy 1: Merge very close vertices (but not boundary vertices)
  if (mergeVertices) {
    mergeCloseVertices(geometry, VERTEX_MERGE_THRESHOLD, meshBoundaryVertices);
  }

  // Strategy 2: Collapse short edges (but not boundary edges)
  if (collapseEdges) {
    collapseShortEdges(geometry, MIN_EDGE_LENGTH, meshBoundaryVertices);
  }

  // Strategy 3: Smooth distorted areas (but not boundary vertices)
  if (smoothDistorted) {
    smoothDistortedAreas(geometry, 3, 0.5, meshBoundaryVertices);
  }

  const endTriangleCount = geometry.index ? geometry.index.count / 3 : 0;

  if (verbose && startTriangleCount !== endTriangleCount) {
    console.log(`[geometryRepair] Repaired geometry: ${startTriangleCount} -> ${endTriangleCount} triangles`);
  }

  // Recompute bounds
  geometry.computeBoundingSphere();
  geometry.computeBoundingBox();

  return geometry;
}
